<template>
  <div class="mentors-list">
    <b-row class="d-flex justify-content-between align-items-center mx-1">
      <div class="d-flex flex-row">
        <h4>
          <template v-if="loading">Loading...</template>
          <template v-else>{{ this.results.edges.length | plural('result') }}</template>
        </h4>
      </div>
      <div class="d-flex flex-row">
        <sort class="mr-4" :options="mentorOrderByEnums" :value="order" @input="v => $emit('orderUpdated', v)"/>
        <toggle-filters class="py-1 px-3" v-model="showHorizontalWhere"/>
      </div>
    </b-row>

    <b-row class="mt-3" v-if="showHorizontalWhere">
      <b-col>
        <filter-block :fields="filterFields" :value="where" @input="filterResults($event)" />
      </b-col>
    </b-row>

    <loading-spinner class="my-5 text-center" v-if="loading"/>

    <b-row v-if="results && Array.isArray(results.edges) && results.edges.length" class="mb-3 p-2">
      <b-col
        v-for="{ entry } in results.edges.filter(({ entry }) => entry && entry.id).slice((page - 1) * limit, (page - 1) * limit + limit)"
        :key="entry.id"
        cols="12"
        md="4"
        lg="3"
        class="p-2"
      >
        <mentor-item
          :mentor="entry"
          @submitContactMentorRequest="($event) => $emit('submitContactMentorRequest', { mentor: entry, form: $event })"
        />
      </b-col>
    </b-row>
    <div v-if="!hidePagination && page && totalPages" class="filter-pagination">
      <p>Page {{ page }} of {{ totalPages }}</p>
      <b-pagination :value="page" @input="pageUpdated($event)" :per-page="limit" :total-rows="total"/>
    </div>
    <b-row v-else class="my-5">
      <b-col cols="12">
        <div class="text-center text-muted">No mentors joined yet.</div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import MentorItem from '@/components/mentors/MentorItem.vue';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import FilterBlock from '@/components/misc/FilterBlock';
import Sort from '@/components/misc/Sort';
import ToggleFilters from '@/components/misc/ToggleFilters';
import { tryNull } from '@src/lib/utils';
import { parseIfPossible } from '@src/lib/imMatching';

export default {
  name: 'MentorsList',
  components: {
    Sort,
    ToggleFilters,
    FilterBlock,
    LoadingSpinner,
    MentorItem,
  },
  props: {
    mentors: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    company: {
      type: Object,
      default: () => null,
    },
    hidePagination: {
      type: Boolean,
      default: () => false,
    },
    defaultShowFilters: {
      type: Boolean,
      default: () => true,
    },
    order: {
      type: String,
      default: () => null,
    },
    mentorOrderByEnums: {
      type: Object,
      default() {
        return {
          'CREATED_DESC': 'Newest first',
          'CREATED_ASC': 'Oldest first',
          'MOST_RELEVANT': 'Most relevant',
        };
      }
    },
    filterFields: {
      type: Array,
      default() {
        const { company: { customFields } = {} } = this;
        const jobTitlesField = Array.isArray(customFields) ? customFields.find(el => {
          return el.group === 'skills' && el.type === 'JOB_TITLES';
        }) : null;
        const jobSkillsField = Array.isArray(customFields) ? customFields.find(el => {
          return el.group === 'skills' && el.type === 'JOB_SKILLS';
        }) : null;
        const jobTitleOptions = jobTitlesField && jobTitlesField.options ? JSON.parse(jobTitlesField.options) : null;
        const jobSkillOptions = jobSkillsField && jobSkillsField.options ? JSON.parse(jobSkillsField.options) : null;
        const filterFields = [
          {
            key: 'jobTitle',
            type: 'STRING',
            name: 'Job Title',
            options: jobTitleOptions,
          },
          {
            key: 'name',
            type: 'STRING',
            name: 'Name',
          },
          {
            key: 'skills',
            type: 'JOB_SKILLS',
            name: this.$root.isCXR ? 'Areas of Expertise or Interest' : 'Skills',
            options: jobSkillOptions,
          },
        ];
        return filterFields;
      },
    },
  },
  watch: {
    mentors() {
      // Temporary solution to re-sort mentors when they are loaded from cache
      // Will be removed once backend matching is implemented
      this.results.edges = this.mentors ? JSON.parse(JSON.stringify(this.mentors)) : [];
      this.total = (this.mentors ? this.mentors : []).length;
      this.totalPages = Math.ceil((this.mentors ? this.mentors : []).length / 10);
    },
  },
  data() {
    return {
      results: {
        edges: this.mentors ? this.mentors : []
      },
      showHorizontalWhere: this.defaultShowFilters === true,
      hideHighlightedJob: false,
      where: {
        jobTitle: null,
        skills: [],
        name: '',
      },
      page: 1,
      limit: 12,
      total: (this.mentors ? this.mentors : []).length,
      totalPages: Math.ceil((this.mentors ? this.mentors : []).length / 10)
    };
  },
  methods: {
    pageUpdated(page) {
      this.page = page;
      this.$scrollTo('body');
    },
    getMentorFieldValue(mentor, type, group) {
      const { customFields } = this.company || {};
      const { customFields: mentorCustomFields } = mentor || {};
      if (Array.isArray(customFields) && customFields.length && Array.isArray(mentorCustomFields)) {
        const field = customFields.find(el => el.type === type && el.group === group);
        const fieldKey = field ? field.key : null;
        const mentorField = fieldKey ? mentorCustomFields.find(({ key }) => key === fieldKey) : null;
        return mentorField ? mentorField.value : null;
      }
      return null;
    },
    filterResults(where) {
      const { isCXR } = this.$root;
      this.where = where;
      this.results.edges = this.mentors.filter(({ entry: mentor }) => mentor && isCXR
        ? filterCxrMentors.call(this, mentor, where)
        : filterMentors.call(this, mentor, where));
      this.total = (this.results.edges ? this.results.edges : []).length;
      this.totalPages = Math.ceil((this.results.edges ? this.results.edges : []).length / 10);
    },
  },
};

function filterMentors(mentor, where) {
  const bool = [];
  if (where.jobTitle && where.jobTitle.length > 1) {
    const jobTitle = this.getMentorFieldValue(mentor, 'JOB_TITLES', 'skills');
    bool.push(jobTitle && jobTitle.toLowerCase().includes(where.jobTitle.toLowerCase()));
  }
  if (Array.isArray(where.skills) && where.skills.length > 0) {
    const skills = parseIfPossible(this.getMentorFieldValue(mentor, 'JOB_SKILLS', 'mentorship'));
    bool.push(Array.isArray(skills) && skills.reduce((out, skill) => where.skills.includes(skill) || out, false));
  }
  if (where.name.length > 1) {
    bool.push(mentor.fullName && `${mentor.fullName}`.toLowerCase().includes(where.name.toLowerCase()));
  }
  return bool.reduce((b, v) => b && v, true);
}
function filterCxrMentors(mentor, where) {
  const bool = [];
  const getCustomField = (fields, key) => (Array.isArray(fields) ? fields : []).find(c => c && c.key === key);
  if (typeof where.jobTitle === 'string' && where.jobTitle.length > 1) {
    const { value } = getCustomField(mentor.customFields, 'CURRENTMOST-RECENT-JOB-TITLE-621792') || {};
    bool.push(value && `${value}`.toLowerCase().includes(where.jobTitle.toLowerCase()));
  }
  if (Array.isArray(where.skills) && where.skills.length > 0) {
    let { value } = getCustomField(mentor.customFields, 'DISCIPLINES-436756') || {};
    value = tryNull(() => JSON.parse(value));
    bool.push(Array.isArray(value) && value.reduce((out, skill) => where.skills.includes(skill) || out, false));
  }
  if (where.name.length > 1) {
    bool.push(mentor.fullName && `${mentor.fullName}`.toLowerCase().includes(where.name.toLowerCase()));
  }
  return bool.reduce((b, v) => b && v, true);
}
</script>

<style lang="scss">
.mentors-list {

  .card.horizontal-where {
    margin-top: 10px;
    margin-bottom: 20px;
  }

  .filter-pagination {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .vertical-where {
    .vertical-sort {
      padding: 10px 20px 0 20px;

      label {
        font-weight: normal;
      }

      .custom-radio {
        margin-right: 0;
        margin-bottom: 10px;
      }
    }

    .results-tag {
      color: rgba(49, 55, 67, 0.5);
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/mentors/MentorsListContainer.vue